import PQueue from 'p-queue';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { HotkeyBindingInfo, ScopedStorageItemInfo } from '../types/obs';
import { AuthContextProvider } from '@streamelements/client-sdk-js';
import { ScopedStorage } from './ScopedStorageService';
import { promisefy } from '../utils/promisefy';
import { getIsHotkeyServiceEnabled } from '../constants/common';

type HotkeySelector = {
    registererTypeName: 'frontend'
    name: HotkeyBindingInfo['name'];
} | {
    registererTypeName: HotkeyBindingInfo['registererTypeName'];
    registererName: NonNullable<HotkeyBindingInfo['registererName']>;
    name: HotkeyBindingInfo['name'];
};

type HotkeyTriggers = NonNullable<HotkeyBindingInfo['triggers']>;
type HotkeyStorageData = Record<string, HotkeyTriggers>;

function getHotkeySelectorKey(selector: HotkeySelector): string {
    if (selector.registererTypeName === 'frontend') {
        return `frontend:${selector.name}`;
    } else {
        return `${selector.registererTypeName}:${selector.name}:${selector.registererName}`;
    }
}

async function getHotkeysScopedStorageItemInfo(authContextProvider: AuthContextProvider): Promise<ScopedStorageItemInfo> {
    const authContext = await authContextProvider.getAuthContext();

    return {
        scope: 'streamelements_multi-streaming',
        container: authContext.channel._id,
        item: 'hotkeys_triggers',
    };
}

async function readHotkeysScopedStorageItem(authContextProvider: AuthContextProvider): Promise<HotkeyStorageData> {
    const itemInfo = await getHotkeysScopedStorageItemInfo(authContextProvider);
    
    try {
        const item = await ScopedStorage.readScopedStorageJsonItem<HotkeyStorageData>(itemInfo);

        return item?.content ?? {};
    } catch {
        return {};
    }
}

async function writeHotkeysScopedStorageItem(authContextProvider: AuthContextProvider, data: HotkeyStorageData): Promise<void> {
    const itemInfo = await getHotkeysScopedStorageItemInfo(authContextProvider);
    
    await ScopedStorage.writeScopedStorageJsonItem<HotkeyStorageData>({
        ...itemInfo,
        content: data,
    });
}

async function readHotkeyDataForSelector(authContextProvider: AuthContextProvider, selector: HotkeySelector): Promise<HotkeyTriggers> {
    return await navigator.locks.request('HotkeyPersistenceManager.readHotkeyDataForSelector', async () => {
        const hotkeys = await readHotkeysScopedStorageItem(authContextProvider);

        const key = getHotkeySelectorKey(selector);

        return hotkeys[key] ?? [];
    }) as HotkeyTriggers;
}

async function writeHotkeyDataForSelector(authContextProvider: AuthContextProvider, selector: HotkeySelector, triggers: HotkeyTriggers): Promise<void> {
    return await navigator.locks.request('HotkeyPersistenceManager.readHotkeyDataForSelector', async () => {
        const hotkeys = await readHotkeysScopedStorageItem(authContextProvider);

        const key = getHotkeySelectorKey(selector);

        hotkeys[key] = triggers;

        await writeHotkeysScopedStorageItem(authContextProvider, hotkeys);
    }) as void;
}

async function getHotkeyBindingBySelector(selector: HotkeySelector): Promise<HotkeyBindingInfo | null> {
    const all = await promisefy(window.host.getAllHotkeyBindings)();

    const existing = all.find((i) => {
        return getHotkeySelectorKey(selector) == getHotkeySelectorKey(i as never);
    });

    return existing ?? null;
}

interface HotkeyPersistenceManager {
}

class HotkeyPersistenceManagerImpl implements HotkeyPersistenceManager{
    private abortController = new AbortController();
    private queue = new PQueue({ concurrency: 1 });
    private selector: HotkeySelector;
    private binding: HotkeyBindingInfo | null = null;
    private triggers: HotkeyTriggers = [];
    private authContextProvider: AuthContextProvider;

    constructor(authContextProvider: AuthContextProvider, selector: HotkeySelector) {
        this.authContextProvider = authContextProvider;
        this.selector = selector;

        this.init();
    }

    public async dispose(): Promise<void> {
        await this.queue.add(async () => {
            this.abortController.abort();
        });

        // Make sure we're done
        await this.queue.add(async () => {
            // await this.syncToStorage();
        });
    }

    private async syncToStorage(): Promise<void> {
        this.binding = await getHotkeyBindingBySelector(this.selector);

        if (!this.binding) {
            console.warn('syncToStorage: could not find hotkey for selector', this.selector);

            return;
        }

        this.triggers = this.binding.triggers ?? [];

        await writeHotkeyDataForSelector(this.authContextProvider, this.selector, this.triggers);
    }

    private async init(): Promise<void> {
        await this.queue.add(async () => {
            this.binding = await getHotkeyBindingBySelector(this.selector);

            if (!this.binding) {
                console.warn('init: could not find hotkey for selector', this.selector);

                return;
            }

            this.triggers = await readHotkeyDataForSelector(this.authContextProvider, this.selector);

            // On first load - set hotkey triggers to the ones in storage
            await promisefy(window.host.setHotkeyBindingTriggers)({
                id: this.binding.id,
                triggers: this.triggers,
            });

            window.addEventListener('hostHotkeyBindingsChanged', async () => {
                await this.queue.add(async () => {
                    await this.syncToStorage();
                });
            }, { signal: this.abortController.signal });
        });
    }
}

export class HotkeyPersistenceService {
    private static queue = new PQueue({ concurrency: 1 });
    private static items: Map<string, HotkeyPersistenceManagerImpl> = new Map();

    public static async startTrackingHotkey(authContextProvider: AuthContextProvider, selector: HotkeySelector): Promise<void> {
        return await this.queue.add(async () => {
            await ensureOBSPresence();

            if (!await getIsHotkeyServiceEnabled())
                return;

            const key = getHotkeySelectorKey(selector);

            if (this.items.has(key)) {
                console.warn('HotkeyPersistenceService: Hotkey already being tracked: ' + JSON.stringify(selector))

                return;
            }

            console.info('HotkeyPersistenceService.startTrackingHotkey: ', selector);
            this.items.set(key, new HotkeyPersistenceManagerImpl(authContextProvider, selector));

            // return this.items.get(key) as HotkeyPersistenceManager;
        });
    }

    public static async stopTrackingHotkey(selector: HotkeySelector): Promise<void> {
        await this.queue.add(async () => {
            if (!await getIsHotkeyServiceEnabled())
                return;

            const key = getHotkeySelectorKey(selector);

            const item = this.items.get(key);

            if (!item) {
                return;
            }

            console.info('HotkeyPersistenceService.stopTrackingHotkey: ', selector);

            await item.dispose();

            this.items.delete(key);
        });
    }
}
